import { ButtonRounded } from 'is24-corecss';
import React, { useCallback } from 'react';
import { css } from '@emotion/css';

import { IconS24ShieldCheckmark24 } from '@is24/cosma-ui-icons';
import { VOUCHER_UPSELL_PARAM } from './constants';
import { VoucherUpsellModal } from './VoucherUpsellModal';
import { useVoucherEligibility } from './useVoucherEligibility.hook';
import { useIntl } from '@/i18n/hooks/useIntl';
import { campaigns } from '@/components/common/campaigns';
import { useLivingBundleEntitlement, usePlusRentEntitlement } from '@/customHooks/useGetHasProduct.hook';

export type VoucherUpsellBannerSource = 'meinPlus' | 'successModal';

interface VoucherUpsellBannerProps {
  variant?: 'default' | 'vibrant';
  onCloseParentModal?: () => void;
  source?: VoucherUpsellBannerSource;
}

const styles = {
  whiteButton: css``,
  content: css`
    gap: 0 !important;
  `,
  noBorder: css`
    border: none !important;
  `,
};

const CAMPAIGN_URL_BY_SOURCE: Record<VoucherUpsellBannerSource, string> = {
  meinPlus: campaigns.WOMENS_DAY_WOHNEN_PLUS_MEIN_PLUS,
  successModal: campaigns.WOMENS_DAY_WOHNEN_PLUS_SUCCESS_MODAL,
};

export const VoucherUpsellBanner = ({
  variant = 'default',
  onCloseParentModal,
  source = 'meinPlus',
}: VoucherUpsellBannerProps) => {
  const isVibrant = variant === 'vibrant';
  const { t } = useIntl();
  const { isEligible } = useVoucherEligibility();
  const hasWohnenPlus = useLivingBundleEntitlement();
  const hasSuchenPlus = usePlusRentEntitlement();
  const canUnlockVoucher = hasWohnenPlus || hasSuchenPlus;

  const handleOpenModal = useCallback(() => {
    if (!canUnlockVoucher) {
      window.location.href = CAMPAIGN_URL_BY_SOURCE[source];
      return;
    }

    if (onCloseParentModal) {
      onCloseParentModal();
      return;
    }
    const url = new URL(window.location.href);
    url.searchParams.set(VOUCHER_UPSELL_PARAM, 'true');
    window.history.pushState({}, '', url);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, [canUnlockVoucher, onCloseParentModal, source]);

  const handleCloseModal = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete(VOUCHER_UPSELL_PARAM);
    window.history.pushState({}, '', url);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, []);

  if (!isEligible) {
    return null;
  }

  return (
    <>
      <div className={`notification notification--neutral notification--flat`}>
        <div className='notification__icon'>
          <IconS24ShieldCheckmark24 />
        </div>
        <div className={`notification__content ${styles.content}`}>
          <div className='font-body-medium-bold'>
            {t({ id: isVibrant ? 'voucherUpsellBanner.vibrant.title' : 'voucherUpsellBanner.title' })}
          </div>
          <div className='notification__message font-body-small-regular'>
            {t({ id: isVibrant ? 'voucherUpsellBanner.vibrant.message' : 'voucherUpsellBanner.message' })}
          </div>
          <div className='notification__actions'>
            <ButtonRounded
              variant={isVibrant ? 'filledDefault' : 'outlineWeak'}
              size={isVibrant ? 'medium' : 'small'}
              onClick={handleOpenModal}
              className={`margin-top-m ${styles.whiteButton}`}
            >
              {t({
                id: canUnlockVoucher ? 'voucherUpsellBanner.cta' : 'voucherUpsellBanner.ctaWithoutWohnenPlus',
              })}
            </ButtonRounded>
          </div>
        </div>
      </div>
      <VoucherUpsellModal onClose={handleCloseModal} />
    </>
  );
};
